import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Archive, Trash2, MailOpen, Reply, ReplyAll, Forward, Paperclip, ImageOff, Star, Download, MoreVertical, Filter as FilterIcon, Ban, MailX, BellOff, Bell, ChevronsUpDown, ChevronsDownUp, PencilLine } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { Avatar, EmptyState, IconButton, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Skeleton } from "@dfws/ui";
import { replyRecipients, type ThreadMessage } from "@dfws/contracts";
import { queryClient, useMessageAction, useMessageBody, useSaveFilter, useThread } from "../../lib/queries";
import { api, ApiFailure } from "../../lib/api";
import { useShortcuts } from "../../command/shortcuts";
import { ComposeWindow } from "../compose/ComposeWindow";
import { showComposer, startReply, toast, useComposers, useIdentities } from "../compose/composeData";
import { SenderBars } from "./SenderBars";
import { useSettings } from "../../settings/store";
import { Lightbox, type LightboxItem } from "./Lightbox";
import { fullDate } from "./format";
import s from "./ThreadView.module.css";

export function ThreadView({ threadKey, fallbackSubject, onBack, showBack }: { threadKey: string | null; fallbackSubject: string; onBack: () => void; showBack: boolean }) {
  const thread = useThread(threadKey);
  const action = useMessageAction();
  const navigate = useNavigate();
  const saveFilter = useSaveFilter();
  const [blocked, setBlocked] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const markedFor = useRef<string | null>(null);
  const settings = useSettings();
  const identities = useIdentities();
  const inline = useComposers().find((c) => c.inlineThread && c.inlineThread === threadKey);
  const [opening, setOpening] = useState(false);

  /** Reply, reply all or forward to one message; the composer opens inside the conversation. */
  const respond = async (messageId: number, mode: "reply" | "replyAll" | "forward") => {
    if (!threadKey || opening) return;
    if (inline) return showComposer(inline.draftId, threadKey);
    setOpening(true);
    try {
      const mine = identities.data?.items ?? [];
      await startReply(messageId, mode, mine, (c) => replyRecipients({ from: c.from, replyTo: c.replyTo, to: c.to, cc: c.cc }, mode === "forward" ? "reply" : mode, mine), threadKey);
    } catch (err) {
      toast({ text: err instanceof ApiFailure ? err.message : "The reply could not be started.", tone: "error" });
    } finally {
      setOpening(false);
    }
  };

  // Opening a conversation marks it read once (optimistic), and expands the newest and any unread messages.
  useEffect(() => {
    const d = thread.data;
    if (!d || markedFor.current === d.key) return;
    markedFor.current = d.key;
    const last = d.messages[d.messages.length - 1];
    // One-time initialisation per conversation from data that arrives asynchronously.
    setExpanded(new Set(d.messages.filter((m) => m.unread || m.id === last?.id).map((m) => m.id)));
    const unread = d.messages.filter((m) => m.unread).map((m) => m.id);
    if (unread.length) action.mutate({ messageIds: unread, action: "read", threadKey: d.key });
  }, [thread.data, action]);

  if (!threadKey) {
    return (
      <div className={s.pane}>
        <EmptyState icon={<MailOpen />} title="No conversation open">
          Pick one from the list, or move with j and k and press Enter.
        </EmptyState>
      </div>
    );
  }

  const d = thread.data;
  const starred = d?.messages.some((m) => m.starred) ?? false;
  const last = d?.messages[d.messages.length - 1];
  const ordered = d ? (settings.threadOrder === "newest" ? [...d.messages].reverse() : d.messages) : [];
  const allOpen = Boolean(d && d.messages.every((m) => expanded.has(m.id)));
  const manyPeople = Boolean(last && [...last.to, ...last.cc].length > 1);
  const primary = settings.defaultReply === "replyAll" && manyPeople ? "replyAll" : "reply";
  const moveThread = (to: "archive" | "trash") => {
    if (!d) return;
    action.mutate({ messageIds: d.messages.map((m) => m.id), action: to, threadKey: d.key });
    toast({ text: to === "archive" ? "Conversation archived" : "Conversation moved to Trash" }, 4000);
    onBack();
  };
  const mute = async () => {
    if (!d) return;
    const muted = !d.muted;
    await api(`/threads/${encodeURIComponent(d.key)}/mute`, { method: "PUT", body: { muted } });
    queryClient.setQueryData(["thread", d.key], { ...d, muted });
    void queryClient.invalidateQueries({ queryKey: ["threads"] });
    toast({ text: muted ? "Muted. New replies stay out of your inbox." : "Unmuted" }, 4000);
  };

  return (
    <article className={s.pane} aria-label={d?.subject || fallbackSubject || "(no subject)"}>
      <div className={s.toolbar} role="toolbar" aria-label="Conversation actions">
        {showBack && <IconButton label="Back to list" shortcut="u" icon={<ArrowLeft />} onClick={onBack} size="sm" />}
        <IconButton label="Archive" icon={<Archive />} size="sm" disabled={!d} onClick={() => moveThread("archive")} />
        <IconButton label="Delete" icon={<Trash2 />} size="sm" disabled={!d} onClick={() => moveThread("trash")} />
        <IconButton
          label={starred ? "Unstar" : "Star"}
          shortcut="s"
          icon={<Star className={clsx(starred && s.starred)} />}
          size="sm"
          pressed={starred}
          disabled={!last}
          onClick={() => last && action.mutate({ messageIds: [last.id], action: starred ? "unstar" : "star", threadKey })}
        />
        <span className={s.spacer} />
        {d && d.messages.length > 1 && (
          <IconButton
            label={allOpen ? "Collapse all" : "Expand all"}
            icon={allOpen ? <ChevronsDownUp /> : <ChevronsUpDown />}
            size="sm"
            onClick={() => setExpanded(allOpen ? new Set(last ? [last.id] : []) : new Set(d.messages.map((m) => m.id)))}
          />
        )}
        {d && <IconButton label={d.muted ? "Unmute conversation" : "Mute conversation"} icon={d.muted ? <Bell /> : <BellOff />} size="sm" pressed={Boolean(d.muted)} onClick={() => void mute()} />}
        {last && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton label="More actions" icon={<MoreVertical />} size="sm" />
            </MenuTrigger>
            <MenuContent>
              <MenuItem icon={<MailX />} onSelect={() => action.mutate({ messageIds: [last.id], action: "unread", threadKey })} shortcut="Shift+U">
                Mark as unread
              </MenuItem>
              <MenuSeparator />
              <MenuItem icon={<FilterIcon />} onSelect={() => void navigate({ to: "/settings/$section", params: { section: "filters" }, search: { from: last.from.email } as never })}>
                Filter messages like this
              </MenuItem>
              <MenuItem
                icon={<Ban />}
                danger
                onSelect={() =>
                  saveFilter.mutate(
                    { enabled: true, criteria: { from: last.from.email }, actions: { spam: true } },
                    { onSuccess: () => setBlocked(last.from.email) },
                  )
                }
              >
                Block {last.from.name || last.from.email}
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
      {blocked && (
        <p className={s.blockedNote} role="status">
          New mail from {blocked} now goes to Spam. You can undo this in Settings, Blocked addresses.
        </p>
      )}
      <div className={s.scroll}>
        <header className={s.header}>
          <h1 className={s.subject} dir="auto">
            {d?.subject || fallbackSubject || "(no subject)"}
          </h1>
          {d && d.messages.length > 1 && <span className={s.count}>{d.messages.length} messages</span>}
        </header>
        {thread.isPending && <MessageSkeleton />}
        {thread.isError && (
          <p className={s.error} role="alert">
            {thread.error.message}
          </p>
        )}
        {d?.muted && <p className={s.blockedNote} role="status">This conversation is muted. New replies go to All mail, not your inbox.</p>}
        {ordered.map((m) => (
          <MessageCard
            key={m.id}
            message={m}
            expanded={expanded.has(m.id)}
            onToggle={() => setExpanded((p) => (p.has(m.id) ? new Set([...p].filter((x) => x !== m.id)) : new Set([...p, m.id])))}
            onRespond={(mode) => void respond(m.id, mode)}
            onDelete={() => {
              action.mutate({ messageIds: [m.id], action: "trash", threadKey: d!.key });
              toast({ text: "Message moved to Trash" }, 4000);
              if (d!.messages.length === 1) onBack();
            }}
          />
        ))}
        {d && inline && <ComposeWindow key={inline.draftId} draftId={inline.draftId} variant="inline" />}
        {d && !inline && last && (
          <div className={s.replyBar}>
            {(primary === "replyAll" ? (["replyAll", "reply"] as const) : manyPeople ? (["reply", "replyAll"] as const) : (["reply"] as const)).map((mode) => (
              <button key={mode} type="button" className={s.replyButton} disabled={opening} onClick={() => void respond(last.id, mode)}>
                {mode === "reply" ? <Reply /> : <ReplyAll />} {mode === "reply" ? "Reply" : "Reply all"}
              </button>
            ))}
            <button type="button" className={s.replyButton} disabled={opening} onClick={() => void respond(last.id, "forward")}>
              <Forward /> Forward
            </button>
          </div>
        )}
        <ThreadKeys enabled={Boolean(last) && !inline} onKey={(mode) => last && void respond(last.id, mode === "reply" ? primary : mode)} />
      </div>
    </article>
  );
}

/** r, a and f answer the newest message while a conversation is open. */
function ThreadKeys({ enabled, onKey }: { enabled: boolean; onKey: (mode: "reply" | "replyAll" | "forward") => void }) {
  const { shortcutsEnabled } = useSettings();
  useShortcuts({ reply: () => onKey("reply"), replyAll: () => onKey("replyAll"), forward: () => onKey("forward") }, enabled && shortcutsEnabled);
  return null;
}

function MessageCard({ message: m, expanded, onToggle, onRespond, onDelete }: { message: ThreadMessage; expanded: boolean; onToggle: () => void; onRespond: (mode: "reply" | "replyAll" | "forward") => void; onDelete: () => void }) {
  const always = useSettings().externalImages === "always";
  const [images, setImages] = useState(always);
  const [viewing, setViewing] = useState<number | null>(null);
  const body = useMessageBody(m.id, images, expanded);
  const recipients = [...m.to, ...m.cc].map((a) => a.name || a.email).join(", ");
  return (
    <section className={clsx(s.message, !expanded && s.collapsed)}>
      <button type="button" className={s.messageHead} onClick={onToggle} aria-expanded={expanded}>
        <Avatar name={m.from.name || m.from.email} email={m.from.email} size={40} />
        <span className={s.fromText}>
          <span className={s.fromLine}>
            <strong dir="auto">{m.from.name || m.from.email}</strong>
            {expanded && m.from.name && <span className={s.email}>&lt;{m.from.email}&gt;</span>}
          </span>
          <span className={s.to} dir="auto">
            {expanded ? `to ${recipients || "undisclosed recipients"}` : m.snippet}
          </span>
        </span>
        <span className={s.meta}>
          {m.hasAttachment && <Paperclip aria-label="Has attachment" />}
          <time dateTime={m.date}>{fullDate(new Date(m.date))}</time>
        </span>
      </button>
      {expanded && (
        <div className={s.msgActions}>
          <IconButton label="Reply" icon={<Reply />} size="sm" onClick={() => onRespond("reply")} />
          <Menu>
            <MenuTrigger asChild>
              <IconButton label="More for this message" icon={<MoreVertical />} size="sm" />
            </MenuTrigger>
            <MenuContent>
              <MenuItem icon={<Reply />} onSelect={() => onRespond("reply")}>Reply</MenuItem>
              <MenuItem icon={<ReplyAll />} onSelect={() => onRespond("replyAll")}>Reply all</MenuItem>
              <MenuItem icon={<Forward />} onSelect={() => onRespond("forward")}>Forward</MenuItem>
              <MenuSeparator />
              <MenuItem icon={<Trash2 />} danger onSelect={onDelete}>Delete this message</MenuItem>
            </MenuContent>
          </Menu>
        </div>
      )}
      {expanded && (
        <div className={s.body}>
          {body.isPending && <Skeleton width="70%" height={12} />}
          {body.isError && (
            <p className={s.error} role="alert">
              This message could not be loaded. {body.error.message}
            </p>
          )}
          {body.data && (
            <>
              {body.data.draftId && (
                <div className={s.imagesBar} role="status">
                  <PencilLine aria-hidden />
                  <span>Draft written in Workspace.</span>
                  <button type="button" onClick={() => showComposer(body.data!.draftId!)}>Continue editing</button>
                </div>
              )}
              <SenderBars message={m} body={body.data} />
              {body.data.blockedImages > 0 && !images && (
                <div className={s.imagesBar} role="status">
                  <ImageOff aria-hidden />
                  <span>Images are hidden to protect your privacy.</span>
                  <button type="button" onClick={() => setImages(true)}>
                    Show images
                  </button>
                </div>
              )}
              {body.data.html ? <MailFrame html={body.data.html} title={`Message from ${m.from.name || m.from.email}`} /> : <pre className={s.plain} dir="auto">{body.data.text ?? ""}</pre>}
              {body.data.attachments.length > 0 && (
                <Attachments messageId={m.id} items={body.data.attachments} onView={setViewing} />
              )}
              {viewing !== null && (
                <Lightbox
                  items={viewables(m.id, body.data.attachments)}
                  index={Math.max(0, viewables(m.id, body.data.attachments).findIndex((v) => v.index === viewing))}
                  onClose={() => setViewing(null)}
                />
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Renders sanitized mail HTML in a sandboxed iframe: no scripts (no allow-scripts), links escape the sandbox into a new tab.
 * allow-same-origin lets us measure the height; the document inherits the page CSP, so only same-origin images load.
 */
function MailFrame({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const dark = typeof document !== "undefined" && document.documentElement.dataset.theme === "dark";
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0;background:#fff;color:#1b1726;font:14px/1.6 "Inter Variable",Inter,system-ui,sans-serif;overflow-wrap:anywhere}
    body{padding:2px 2px 8px}img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}
    img[data-blocked-src]{display:inline-block;min-width:24px;min-height:24px;background:#f3f1f7;outline:1px dashed #d3cddd}
    a{color:#7a2fb5}blockquote{margin:0 0 0 .8ex;border-left:1px solid #d3cddd;padding-left:1ex;color:#5b5568}
  </style></head><body dir="auto">${html}</body></html>`;

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    let ro: ResizeObserver | undefined;
    const measure = () => {
      const b = frame.contentDocument?.documentElement;
      if (b) setHeight(Math.min(20000, Math.max(40, b.scrollHeight)));
    };
    const onLoad = () => {
      measure();
      const body = frame.contentDocument?.body;
      if (body) {
        ro = new ResizeObserver(measure);
        ro.observe(body);
      }
    };
    frame.addEventListener("load", onLoad);
    return () => {
      frame.removeEventListener("load", onLoad);
      ro?.disconnect();
    };
  }, [doc]);

  return (
    <iframe
      ref={ref}
      title={title}
      className={clsx(s.frame, dark && s.frameDark)}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={doc}
      style={{ height }}
      referrerPolicy="no-referrer"
    />
  );
}

type Att = { index: number; filename: string; contentType: string; size: number };
const isImage = (a: Att) => /^image\/(png|jpe?g|gif|webp)$/i.test(a.contentType);
const isPdf = (a: Att) => a.contentType.toLowerCase() === "application/pdf";
const attUrl = (id: number, a: Att, inline = false) => `/api/v1/messages/${id}/attachments/${a.index}${inline ? "?inline=1" : ""}`;

function viewables(messageId: number, items: Att[]): LightboxItem[] {
  return items.filter(isImage).map((a) => ({ index: a.index, src: attUrl(messageId, a, true), download: attUrl(messageId, a), name: a.filename, size: formatSize(a.size) }));
}

/** Image attachments as thumbnails that open a lightbox; PDFs open in the browser viewer; everything else downloads. */
function Attachments({ messageId, items, onView }: { messageId: number; items: Att[]; onView: (index: number) => void }) {
  return (
    <ul className={s.attachments} aria-label="Attachments">
      {items.map((a) =>
        isImage(a) ? (
          <li key={a.index}>
            <button type="button" className={s.thumb} onClick={() => onView(a.index)} aria-label={`Preview ${a.filename}`}>
              <img src={attUrl(messageId, a, true)} alt="" loading="lazy" />
              <span className={s.thumbName}>{a.filename}</span>
            </button>
          </li>
        ) : (
          <li key={a.index}>
            <a
              className={s.attachment}
              href={attUrl(messageId, a, isPdf(a))}
              {...(isPdf(a) ? { target: "_blank", rel: "noopener" } : { download: a.filename })}
            >
              <Paperclip aria-hidden />
              <span className={s.attName}>{a.filename}</span>
              <span className={s.attSize}>{formatSize(a.size)}</span>
              <Download aria-hidden className={s.attDl} />
            </a>
          </li>
        ),
      )}
    </ul>
  );
}

function MessageSkeleton() {
  return (
    <div className={s.message} aria-busy="true">
      <div className={s.messageHead}>
        <Skeleton width={40} height={40} radius={20} />
        <span className={s.fromText}>
          <Skeleton width="30%" height={12} />
          <Skeleton width="20%" height={10} />
        </span>
      </div>
      <div className={s.body}>
        <Skeleton width="90%" height={12} />
        <Skeleton width="80%" height={12} />
        <Skeleton width="60%" height={12} />
      </div>
    </div>
  );
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
